import { ChangePasswordRequestBody, UpdateSystemUserRequestBody } from '@engagement-web-app/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Calendar1, Save, ShieldOff, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { useSystemAuthentication } from '@/auth/SystemAuthenticationProvider';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Link } from '@/components/Link';
import { MfaResetDialog } from '@/components/MfaResetDialog';
import { Page } from '@/components/Page';
import { Pill } from '@/components/Pill';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { changePassword, requestMfaReset, requestPasswordReset, updateSystemUser } from '@/queries/system-users';
import { getFullName } from '@/utils';
import * as styles from './index.css';

const updateSystemUserSchema = UpdateSystemUserRequestBody.pick({
    lastName: true,
    firstName: true,
    email: true,
    phoneNumber: true,
});

type UpdateSystemUserSchema = z.infer<typeof updateSystemUserSchema>;

const changePasswordSchema = z
    .object({
        currentPassword: ChangePasswordRequestBody.shape.currentPassword,
        newPassword: ChangePasswordRequestBody.shape.newPassword,
        confirmPassword: ChangePasswordRequestBody.shape.newPassword,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: 'パスワードが一致しません',
        path: ['confirmPassword'],
    });

type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

export const Route = createFileRoute('/system/_auth/profile/')({
    component: RouteComponent,
});

function RouteComponent() {
    const currentUser = useSystemAuthentication();
    const [isMfaResetDialogOpen, setIsMfaResetDialogOpen] = useState(false);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors, isDirty },
    } = useForm({
        defaultValues: {
            lastName: currentUser.lastName,
            firstName: currentUser.firstName,
            email: currentUser.email,
            phoneNumber: currentUser.phoneNumber ?? '',
        },
        resolver: zodResolver(updateSystemUserSchema),
    });

    const {
        handleSubmit: handlePasswordSubmit,
        register: registerPassword,
        reset: resetPassword,
        formState: { errors: passwordErrors, isDirty: isPasswordDirty },
    } = useForm<ChangePasswordSchema>({
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        resolver: zodResolver(changePasswordSchema),
    });

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (data: UpdateSystemUserRequestBody) => updateSystemUser(currentUser.uuid, data),
        onSuccess: ({ lastName, firstName, email, phoneNumber }) => {
            reset({
                lastName,
                firstName,
                email,
                phoneNumber: phoneNumber ?? '',
            });
            showSuccessToast({ title: '個人情報', description: '個人情報が更新されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: '個人情報', description: '個人情報の更新に失敗しました。' });
        },
    });

    const { mutateAsync: changePasswordAsync, isPending: isChangingPassword } = useMutation({
        mutationFn: (data: ChangePasswordRequestBody) => changePassword(data),
        onSuccess: () => {
            resetPassword();
            showSuccessToast({ title: 'パスワードの設定', description: 'パスワードが更新されました。' });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'パスワードの設定', description: 'パスワードの更新に失敗しました。' });
        },
    });

    const { mutateAsync: requestPasswordResetAsync, isPending: isRequestingPasswordReset } = useMutation({
        mutationFn: () => requestPasswordReset(),
        onSuccess: () => {
            showSuccessToast({
                title: 'パスワード再設定',
                description: '登録されているメールアドレスにパスワード変更リンクを送信しました。',
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'パスワード再設定', description: 'パスワード再設定メールの送信に失敗しました。' });
        },
    });

    const { mutateAsync: requestMfaResetAsync, isPending: isRequestingMfaReset } = useMutation({
        mutationFn: () => requestMfaReset(),
        onSuccess: () => {
            setIsMfaResetDialogOpen(false);
            showSuccessToast({
                title: 'MFAリセット',
                description: '多要素認証（MFA）をリセットしました。次回ログイン時に再設定が必要です。',
            });
        },
        onError: (error) => {
            console.error(error);
            showErrorToast({ title: 'MFAリセット', description: 'MFAのリセットに失敗しました。' });
        },
    });

    const onSubmit = async (formValues: UpdateSystemUserSchema) => {
        await mutateAsync({
            lastName: formValues.lastName,
            firstName: formValues.firstName,
            email: formValues.email,
            phoneNumber: formValues.phoneNumber,
        });
    };

    const onPasswordSubmit = async (formValues: ChangePasswordSchema) => {
        await changePasswordAsync({
            currentPassword: formValues.currentPassword,
            newPassword: formValues.newPassword,
        });
    };

    return (
        <Page.Root>
            <Page.Header title="マイページ" />

            <Page.Content>
                <section className={styles.section}>
                    <div className={styles.header}>
                        {/* TODO: Display user avatar */}
                        <Avatar size="xl" />
                        <div>
                            <h2 className={styles.name}>{getFullName(currentUser)}</h2>
                            <div className={styles.role}>{currentUser.role.name}</div>
                            <div className={styles.joinedAt}>
                                <Icon icon={Calendar1} size="xs" />
                                {format(currentUser.createdAt, 'yyyy/MM/dd')}
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionContent}>
                        <div className={styles.subHeader}>
                            <h2 className={styles.sectionTitle}>個人情報</h2>
                        </div>

                        <Form.Root onSubmit={handleSubmit(onSubmit)}>
                            <Form.Section>
                                <Form.Row>
                                    <Form.Field
                                        label="氏"
                                        control={(props) => (
                                            <Input {...props} {...register('lastName')} placeholder="山田" />
                                        )}
                                        error={errors.lastName?.message}
                                    />

                                    <Form.Field
                                        label="名"
                                        control={(props) => (
                                            <Input {...props} {...register('firstName')} placeholder="太郎" />
                                        )}
                                        error={errors.firstName?.message}
                                    />
                                </Form.Row>

                                <Form.Field
                                    label="メールアドレス"
                                    control={(props) => (
                                        <Input
                                            {...props}
                                            {...register('email')}
                                            placeholder="yamada.taro@example.com"
                                        />
                                    )}
                                    error={errors.email?.message}
                                />

                                <Form.Field
                                    label="電話番号"
                                    control={(props) => (
                                        <Input {...props} {...register('phoneNumber')} placeholder="090-1234-5678" />
                                    )}
                                    error={errors.phoneNumber?.message}
                                />
                            </Form.Section>

                            <div className={styles.buttonRow}>
                                <Button type="submit" disabled={!isDirty || isPending}>
                                    <Icon icon={Save} size="sm" />
                                    {isPending ? '更新中...' : '個人情報を更新する'}
                                </Button>
                            </div>
                        </Form.Root>
                    </div>
                </section>

                <section className={styles.section}>
                    <div className={styles.subHeader}>
                        <h2 className={styles.sectionTitle}>パスワードの設定</h2>
                        {/* TODO: Check if MFA is enabled */}
                        <Pill variant="secondary" className={styles.mfaPill}>
                            <Icon icon={Smartphone} size="xs" />
                            2段階認証が有効
                        </Pill>
                    </div>

                    <p className={styles.sectionDescription}>アカウントのパスワードの再設定をこちらから行えます。</p>

                    <Form.Root className={styles.sectionContent} onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                        <Form.Section>
                            <Form.Field
                                label="現在のパスワード"
                                control={(props) => (
                                    <Input
                                        {...props}
                                        {...registerPassword('currentPassword')}
                                        type="password"
                                        placeholder="現在のパスワードを入力してください。"
                                    />
                                )}
                                error={passwordErrors.currentPassword?.message}
                            />

                            <Form.Field
                                label="新しいパスワード"
                                control={(props) => (
                                    <Input
                                        {...props}
                                        {...registerPassword('newPassword')}
                                        type="password"
                                        placeholder="新しいパスワードを入力してください。（8文字以上）"
                                    />
                                )}
                                error={passwordErrors.newPassword?.message}
                            />

                            <Form.Field
                                label="新しいパスワードの確認"
                                control={(props) => (
                                    <Input
                                        {...props}
                                        {...registerPassword('confirmPassword')}
                                        type="password"
                                        placeholder="もう一度、新しいパスワードを入力してください。"
                                    />
                                )}
                                error={passwordErrors.confirmPassword?.message}
                            />
                        </Form.Section>

                        <div className={styles.buttonRow}>
                            <Button type="submit" disabled={!isPasswordDirty || isChangingPassword}>
                                <Icon icon={Save} size="sm" />
                                {isChangingPassword ? '変更中...' : 'パスワードを変更する'}
                            </Button>
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!isRequestingPasswordReset) {
                                        requestPasswordResetAsync();
                                    }
                                }}
                                disabled={isRequestingPasswordReset}
                            >
                                {isRequestingPasswordReset ? '送信中...' : 'パスワードを忘れました'}
                            </Link>

                            <Button
                                type="button"
                                variant="danger"
                                onClick={() => setIsMfaResetDialogOpen(true)}
                                disabled={isRequestingMfaReset}
                            >
                                <Icon icon={ShieldOff} size="sm" />
                                MFAをリセットする
                            </Button>
                        </div>
                    </Form.Root>

                    <MfaResetDialog
                        target={isMfaResetDialogOpen ? { name: getFullName(currentUser) } : null}
                        isSubmitting={isRequestingMfaReset}
                        onOpenChange={setIsMfaResetDialogOpen}
                        onSubmit={async () => {
                            await requestMfaResetAsync();
                        }}
                        isSelf
                    />
                </section>
            </Page.Content>
        </Page.Root>
    );
}
